import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/** Input, select and textarea styling shared by the contact and sales forms. */
export function fieldClass(invalid?: boolean, className?: string) {
  return cn(
    "block w-full rounded-btn border bg-white px-3.5 py-2.5 text-body text-navy placeholder:text-slate/70 transition-colors",
    "focus:border-navy focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy",
    invalid ? "border-fail" : "border-line hover:border-slate/60",
    className,
  );
}

/**
 * A labelled form field with an optional hint and an inline error. The error
 * paragraph id is `${id}-error`; point the control's aria-describedby at it.
 */
export function Field({
  id,
  label,
  hint,
  error,
  optional = false,
  className,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  error?: string;
  optional?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={className}>
      <label htmlFor={id} className="mb-1.5 block text-small font-medium text-navy">
        {label}
        {optional && <span className="font-normal text-slate"> (optional)</span>}
      </label>
      {children}
      {hint && !error && (
        <p id={`${id}-hint`} className="mt-1.5 text-small text-slate">
          {hint}
        </p>
      )}
      {error && (
        <p id={`${id}-error`} className="mt-1.5 text-small font-medium text-fail">
          {error}
        </p>
      )}
    </div>
  );
}

/** aria-describedby value for a control rendered inside <Field>. */
export function describedBy(id: string, error?: string, hint?: boolean) {
  if (error) return `${id}-error`;
  return hint ? `${id}-hint` : undefined;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const validators = {
  name: (v: string) => (v.trim() ? undefined : "Enter your name so we know who to reply to."),
  email: (v: string) => {
    if (!v.trim()) return "Enter your work email. We reply to this address.";
    if (!EMAIL_RE.test(v.trim())) return "Enter an email in the form name@company.com.";
    return undefined;
  },
  company: (v: string) => (v.trim() ? undefined : "Enter your company name."),
  seats: (v: string) => {
    if (!v.trim()) return "Enter how many people will use Stirrup, for example 60.";
    if (!/^\d+$/.test(v.trim()) || Number(v) < 1) return "Enter seats as a whole number, for example 60.";
    return undefined;
  },
  topic: (v: string) => (v ? undefined : "Choose a topic so we can route your message."),
  message: (v: string) =>
    v.trim().length >= 10 ? undefined : "Add a sentence or two (at least 10 characters) about what you need.",
};
